import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import type { CitationGeometry } from "../../../components/common/citations/citationHighlight";
import { CitationViewerPanel } from "../components/CitationViewerPanel";

/**
 * The one document panel open beside Casey, and what opened it.
 *
 * Owned here rather than by each card because the panel is docked rather than
 * modal. A card holding its own would mean two panels stacked in the same place
 * as soon as the client opened a second suggestion, with no way to tell which
 * document they were looking at -- a modal hid that problem by making the second
 * click impossible. The chip for an uploaded file opens the same one panel, so
 * a document and a suggestion cannot end up beside each other either.
 */

export type OpenCitation = {
  /**
   * Identifies whatever opened this, so it can show as the thing being viewed:
   * a suggestion's own id, or the attachment's when a chip opened the document
   * whole.
   */
  citationId: string;
  file: string;
  /** Absent when showing a document rather than one value found inside it. */
  geometry?: CitationGeometry | null;
  /** 1-based, as Extend reports it. */
  page?: number | null;
  filename?: string | null;
};

type CitationViewer = {
  toggle: (citation: OpenCitation) => void;
  close: () => void;
  openCitationId: string | null;
};

const CitationViewerContext = createContext<CitationViewer | null>(null);

/**
 * Time to animate out. The panel stays mounted this long after closing so it
 * leaves the way it arrived rather than blinking out.
 */
const CLOSE_MS = 200;

export function CitationViewerProvider({
  isChatOpen,
  children
}: {
  isChatOpen: boolean;
  children: React.ReactNode;
}) {
  const [citation, setCitation] = useState<OpenCitation | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current === null) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const close = useCallback(() => {
    if (closeTimer.current !== null) return;
    setIsClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setCitation(null);
      setIsClosing(false);
      closeTimer.current = null;
    }, CLOSE_MS);
  }, []);

  const toggle = useCallback(
    (next: OpenCitation) => {
      // Clicking the snippet of the document already showing puts it away, which
      // is the close most clients will reach for before the header's button.
      if (!isClosing && citation?.citationId === next.citationId) {
        close();
        return;
      }
      clearCloseTimer();
      setIsClosing(false);
      setCitation(next);
    },
    [citation, clearCloseTimer, close, isClosing]
  );

  // A document left floating beside a closed chat belongs to nothing.
  useEffect(() => {
    if (isChatOpen) return;
    clearCloseTimer();
    setCitation(null);
    setIsClosing(false);
  }, [clearCloseTimer, isChatOpen]);

  const value = useMemo(
    () => ({
      toggle,
      close,
      openCitationId: isClosing ? null : citation?.citationId ?? null
    }),
    [citation, close, isClosing, toggle]
  );

  return (
    <CitationViewerContext.Provider value={value}>
      {children}
      {citation ? (
        <CitationViewerPanel
          key={citation.citationId}
          file={citation.file}
          geometry={citation.geometry}
          page={citation.page}
          filename={citation.filename}
          isOpen={!isClosing}
          onClose={close}
        />
      ) : null}
    </CitationViewerContext.Provider>
  );
}

const NO_VIEWER: CitationViewer = {
  toggle: () => undefined,
  close: () => undefined,
  openCitationId: null
};

/**
 * Opening and closing the docked document.
 *
 * Falls back to doing nothing outside a provider, so a card rendered on its own
 * still shows its snippet rather than throwing.
 */
export function useCitationViewer(): CitationViewer {
  return useContext(CitationViewerContext) ?? NO_VIEWER;
}
